import { useEffect, useState } from 'react';
import { SECTION_ICON_STYLE } from '../components/SectionCard/sectionIconStyle';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import InlineLoader from '../components/Loader/InlineLoader';
import PatientImage from '../components/Patient/PatientImage';
import Table from '../components/Listing/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRotateLeft, faCircleCheck, faClock, faXmark,
    faInfoCircle, faUser, faPills, faCoins, faReceipt, faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';
import useRecordNeighbors from '../hooks/useRecordNeighbors';
import RecordNav from '../components/RecordNav/RecordNav';
import './InvoiceDetails.scss';

const fmt = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const getStatusMap = (t) => ({
    APPROVED: { label: t('return_details.status.approved'), cls: 'inv-badge--green', icon: faCircleCheck },
    PENDING:  { label: t('Pending'),                         cls: 'inv-badge--amber', icon: faClock       },
    REJECTED: { label: t('return_details.status.rejected'), cls: 'inv-badge--red',   icon: faXmark       },
});

const getTypeMeta = (t) => ({
    product: { label: t('return_details.type.product'), bg: '#eff6ff', color: '#1d4ed8' },
    service: { label: t('return_details.type.service'), bg: '#fdf4ff', color: '#7e22ce' },
});

const FieldRow = ({ label, children }) => (
    <div className="inv-field-row">
        <span className="inv-field-label">{label}</span>
        <span className="inv-field-value">{children}</span>
    </div>
);

const returnPath = (targetId) => `/returns/${targetId}`;

export default function ReturnDetails() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [ret, setRet] = useState(null);
    const [loading, setLoading] = useState(true);
    const { setTitle } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();

    // Previous / next return (header arrows + ← → keys)
    const { neighbors, goTo } = useRecordNeighbors(`/api/v1/return/${id}/neighbors`, returnPath);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/return/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setRet(data);
                setTitle(data.reference);
                document.title = data.reference;
                setBreadcrumb([
                    { label: t('Returns'), url: '/returns' },
                    { label: data.reference },
                ]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, t]);

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!ret) return <div className="page-content text-muted p-4">{t('return_details.not_found')}</div>;

    const STATUS_MAP = getStatusMap(t);
    const TYPE_META = getTypeMeta(t);
    const status = STATUS_MAP[ret.status] ?? { label: ret.status ?? '—', cls: 'inv-badge--neutral', icon: faClock };
    const items = (ret.items ?? []).map((item, i) => ({
        ...item,
        _index: i + 1,
        _lineTotal: (item.unitPrice ?? 0) * (item.quantity ?? 1),
    }));

    const returnDate = ret.createdAt
        ? new Date(ret.createdAt).toLocaleDateString('fr-MA', { day: '2-digit', month: 'long', year: 'numeric' })
        : '—';

    const patient = ret.patientId
        ? { id: ret.patientId, firstName: ret.patientFirstName, lastName: ret.patientLastName, phoneNumber: ret.patientPhone }
        : null;

    return (
        <div className="page-content inv-detail">

            {/* ── Header card ── */}
            <div className="inv-header-card inv-header-card--hero">
                <div className="inv-header-body">
                    <div className="inv-avatar" style={{ background: '#fef2f2', color: '#dc2626' }}>
                        <FontAwesomeIcon icon={faRotateLeft} />
                    </div>
                    <div>
                        <p className="inv-number">{ret.reference}</p>
                        <p className="inv-date">{returnDate}</p>
                        <span className={`inv-badge ${status.cls}`}>
                            <FontAwesomeIcon icon={status.icon} />
                            {status.label}
                        </span>
                    </div>
                    <RecordNav neighbors={neighbors} onNavigate={goTo} noun="Retour" />
                </div>

                {/* ── Totaux ── */}
                <div className="inv-totals-grid">
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCoins} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('returns.column.amount')}</div>
                            <div className="inv-total-value" style={{ color: '#dc2626' }}>
                                {fmt(ret.total)} <small>dhs</small>
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faPills} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('returns.column.items')}</div>
                            <div className="inv-total-value">{items.length}</div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faReceipt} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Order')}</div>
                            <div className="inv-total-value" style={{ fontSize: 14 }}>
                                {ret.orderId
                                    ? <NavLink to={`/order/${ret.orderId}`} className="inv-link">{ret.orderReference}</NavLink>
                                    : <span style={{ color: '#9ca3af' }}>—</span>}
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('return_details.field.date')}</div>
                            <div className="inv-total-value" style={{ fontSize: 14 }}>{returnDate}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Info columns ── */}
            <div className="inv-col-layout">

                <div className="inv-section-card">
                    <div className="inv-section-title">
                        <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                        {t('return_details.section.info')}
                    </div>
                    <FieldRow label={t('returns.column.reference')}>{ret.reference}</FieldRow>
                    <FieldRow label={t('return_details.field.date')}>{returnDate}</FieldRow>
                    {ret.orderReference && (
                        <FieldRow label={t('Order')}>
                            <NavLink to={`/order/${ret.orderId ?? ''}`} className="inv-link">
                                {ret.orderReference}
                            </NavLink>
                        </FieldRow>
                    )}
                    <FieldRow label={t('Status')}>
                        <span className={`inv-badge ${status.cls}`}>
                            <FontAwesomeIcon icon={status.icon} /> {status.label}
                        </span>
                    </FieldRow>
                    <FieldRow label={t('return_details.field.reason')}>{ret.reason || '—'}</FieldRow>
                </div>

                {patient ? (
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            {t('Patient')}
                        </div>
                        <NavLink to={`/patient/${patient.id}`} className="inv-patient-card">
                            <PatientImage patient={patient} />
                            <div>
                                <p className="inv-patient-name">
                                    {patient.firstName} {patient.lastName}
                                </p>
                                <p className="inv-patient-sub">{patient.phoneNumber ?? '—'}</p>
                            </div>
                        </NavLink>
                    </div>
                ) : (
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            {t('Patient')}
                        </div>
                        <p style={{ padding: '12px 20px', color: '#9ca3af', fontSize: 13 }}>{t('return_details.anonymous_visitor')}</p>
                    </div>
                )}
            </div>

            {/* ── Items table ── */}
            <div className="inv-section-card">
                <div className="inv-section-title">
                    <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                        <FontAwesomeIcon icon={faPills} />
                    </span>
                    {t('return_details.section.items')}
                    <span className="inv-count-badge">{items.length}</span>
                </div>
                <Table
                    mode="client"
                    data={items}
                    columns={[
                        {
                            Header: '#',
                            accessor: '_index',
                            className: 'text-center',
                            Cell: (row) => <span style={{ color: '#9ca3af', fontSize: 12 }}>{row._index}</span>,
                        },
                        {
                            Header: t('return_details.column.item'),
                            accessor: 'itemName',
                            Cell: (row) => {
                                const typeMeta = TYPE_META[row.itemType?.toLowerCase()] ?? { label: row.itemType, bg: '#f3f4f6', color: '#6b7280' };
                                const path = row.itemType?.toLowerCase() === 'product' ? `/products/${row.itemId}` : null;
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {path
                                            ? <NavLink to={path} style={{ fontWeight: 700, color: '#111827' }}>{row.itemName}</NavLink>
                                            : <span style={{ fontWeight: 700, color: '#111827' }}>{row.itemName}</span>}
                                        <span style={{ fontSize: 11, fontWeight: 600, background: typeMeta.bg, color: typeMeta.color, padding: '2px 8px', borderRadius: 999 }}>
                                            {typeMeta.label}
                                        </span>
                                    </div>
                                );
                            },
                        },
                        {
                            Header: t('return_details.column.qty'),
                            accessor: 'quantity',
                            className: 'text-center',
                            Cell: (row) => (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: 28, height: 24,
                                    background: '#fef2f2', color: '#dc2626',
                                    borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '0 8px',
                                }}>
                                    −{row.quantity}
                                </span>
                            ),
                        },
                        {
                            Header: t('return_details.column.unit_price'),
                            accessor: 'unitPrice',
                            className: 'text-end',
                            Cell: (row) => <span>{fmt(row.unitPrice)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span></span>,
                        },
                        {
                            Header: t('Total'),
                            accessor: '_lineTotal',
                            className: 'text-end',
                            Cell: (row) => <span style={{ fontWeight: 600, color: '#dc2626' }}>−{fmt(row._lineTotal)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span></span>,
                        },
                    ]}
                />
            </div>
        </div>
    );
}
